import { useState } from "react";
import { Icon } from "../icon/icon";

export type TestimonialCarouselSlide = {
  quote: string;
  name: string;
  company: string;
  photo?: string;
};

export const TestimonialCarousel = ({
  slides,
  className = "",
}: {
  slides: TestimonialCarouselSlide[];
  className?: string;
}) => {
  const [index, setIndex] = useState(0);
  const slide = slides[index];
  const canNavigate = slides.length > 1;

  const goPrev = () => {
    setIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goNext = () => {
    setIndex((prev) => (prev + 1) % slides.length);
  };

  if (!slide) return null;

  return (
    <div
      className={`relative overflow-hidden rounded-[24px] bg-[linear-gradient(135deg,#2C6BEA,#1B4FC4)] ${className}`}
    >
      <div className="flex flex-col gap-10 p-6 md:flex-row md:items-center md:gap-8 md:p-12">
        <div className="order-2 flex flex-1 flex-col gap-8 md:order-1">
          <p className="font-dela text-[32px] leading-[38px] tracking-[-0.64px] text-[#A6E2F4] md:text-[40px] md:leading-[48px] md:tracking-[-0.8px]">
            {slide.quote}
          </p>
          <p className="font-onest text-[20px] font-semibold leading-6 tracking-[-0.4px] text-white">
            — {slide.name}, {slide.company}
          </p>

          {canNavigate ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F3F4] text-primary transition-colors duration-300 hover:bg-white"
              >
                <Icon svg="arrow-left" width={24} height={24} />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next testimonial"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F3F4] text-primary transition-colors duration-300 hover:bg-white"
              >
                <Icon svg="arrow-right" width={24} height={24} />
              </button>
            </div>
          ) : undefined}
        </div>

        <div className="relative order-1 mx-auto aspect-square w-full max-w-[340px] shrink-0 md:order-2 md:mx-0 md:w-[380px]">
          {slide.photo ? (
            <img
              src={slide.photo}
              alt={slide.name}
              className="h-full w-full rounded-[24px] object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-[24px] bg-white/10">
              <span className="font-dela text-5xl text-white/70">
                {slide.name.charAt(0)}
              </span>
            </div>
          )}
          <span className="pointer-events-none absolute -right-3 -top-3 flex h-16 w-16 items-center justify-center">
            <Icon
              svg="star-badge"
              width={64}
              height={64}
              className="absolute inset-0 text-[#FED106]"
            />
            <Icon svg="party-popper" width={24} height={24} className="relative text-primary" />
          </span>
          <span className="absolute -bottom-3 -left-3 whitespace-nowrap rounded-full bg-[#A6E2F4] px-[14px] py-[6px] font-dela text-[24px] leading-[28px] tracking-[-0.48px] text-ink [transform:rotate(-3.58deg)]">
            {slide.name}
          </span>
        </div>
      </div>
    </div>
  );
};
